import LiveModal from 'live/components/ui/Modal'
import i18n from 'live/services/i18n'
import React, { useEffect, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import styled from 'styled-components'
import { PresetType } from '../ModelsConfigTypes'

type DefaultModalProps = {
    show: boolean
    close: () => void
    save: (name: string) => void
    name?: string
}

type PresetModalProps = DefaultModalProps & {
    title: string
    children: React.ReactNode
    titleIcon?: string
    iconColor?: string
    buttonText?: string
    buttonColor?: string
}

type NewPresetModalProps = DefaultModalProps & {
    save: (name?: string) => void
    duplicated: boolean
    presetList: PresetType[]
}

type ActivatePresetModalProps = DefaultModalProps & {
    activePresetName: string
}

function PresetModal({
    title,
    close,
    save,
    show,
    children,
    titleIcon,
    iconColor,
    buttonText,
    buttonColor
}: PresetModalProps): React.ReactElement {

    return (
        <StyledModal show={show}>
            <ModalHeader>
                <Title>
                    <i style={{ color: iconColor }} className={titleIcon} />
                    <h3>{i18n(title)}</h3>
                </Title>
            </ModalHeader>
            <Modal.Body>
                {children}
            </Modal.Body>
            <Modal.Footer>
                <StyledButton className="btn-primary" style={{ backgroundColor: buttonColor }} onClick={save}>
                    {i18n(buttonText ? buttonText : "save")}
                </StyledButton>
                <StyledButton onClick={close}>{i18n("cancel")}</StyledButton>
            </Modal.Footer>
        </StyledModal>
    )
}

export function NewPresetModal({
    save,
    close,
    show,
    duplicated,
    name,
    presetList
}: NewPresetModalProps): React.ReactElement {

    const [presetName, setPresetName] = useState('')
    const [errorMessage, setErrorMessage] = useState('')

    const handleSave = () => {
        if (!presetName) {
            setErrorMessage('')
            return
        } else if (!!presetList?.find(p => p.name === presetName)) {
            setErrorMessage('That preset name already exist! Please choose another one.')
            return
        }
        setErrorMessage('')
        setPresetName('')
        save(presetName)
    }

    const handleClose = () => {
        setErrorMessage('')
        if (!name) setPresetName('')
        close()
    }

    useEffect(
        () => {
            if (duplicated) {
                let i = 1
                let newName = name && duplicated ? `${name}-copy` : name ? name : ''

                while (presetList?.filter(item => item.name === newName).length !== 0) {

                    newName = (name && duplicated ? `${name}-copy` : name ? name : '') + `(${i})`
                    i += 1;
                }
                setPresetName(newName)
            }
        }, [name, duplicated]
    )

    return (
        <PresetModal
            title={name && duplicated ? "Duplicate Preset" : name ? "Change Preset" : "New Preset"}
            save={handleSave}
            close={handleClose}
            show={show}
        >
            <Container>
                <h3>{i18n("Name")}</h3>
                <input
                    type="text"
                    value={presetName}
                    onChange={(e) => setPresetName(e.target.value)}
                    placeholder="Preset Name..."
                    style={{ border: errorMessage && "solid 1px var(--red-500)" }}
                />
                {errorMessage && <p><i className="fas fa-exclamation-triangle" />{i18n(errorMessage)}</p>}
            </Container>
        </PresetModal>
    )
}

export function DeletePresetModal({
    name,
    save,
    close,
    show
}: DefaultModalProps): React.ReactElement {

    const [inputValue, setInputValue] = useState('')

    const handleClose = () => {
        setInputValue('')
        close()
    }

    const handleSave = () => {
        if (inputValue === 'REMOVE') {
            setInputValue('')
            save(inputValue)
        }
    }

    return (
        <PresetModal
            title="Delete Preset"
            save={handleSave}
            close={handleClose}
            show={show}
            titleIcon="fas fa-exclamation-circle"
            iconColor="var(--red-700)"
            buttonText="Confirm deletion"
            buttonColor="var(--red-500)"
        >
            <DeleteContainer>
                <h3>{i18n("Are you sure you want remove the preset") + `"${name}"?`}</h3>
                <h3>{i18n("This action cannot be undone.")}</h3>
                <h3>
                    {i18n("If you really want to delete the preset, type ")}
                    <b>{i18n("REMOVE")}</b>
                    {i18n(" in the field below before completing the action:")}
                </h3>
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                />
            </DeleteContainer>
        </PresetModal>
    )
}

export function ActivatePresetModal({
    show,
    save,
    close,
    name,
    activePresetName
}: ActivatePresetModalProps): React.ReactElement {

    return (
        <PresetModal
            title={i18n("Active Preset?")}
            save={save}
            close={close}
            show={show}
            titleIcon="far fa-exclamation-triangle"
            iconColor="#F89300"
        >
            <p>
                {i18n("_preset_ative_warn", { oldPreset: activePresetName, newPreset: name })}
            </p>
        </PresetModal>
    )
}

const StyledModal = styled(LiveModal)`
    margin: 0 auto;
    width: 400px;
`

const ModalHeader = styled(Modal.Header)`
    display: flex;
    justify-content: flex-start;
    align-items: center;

    > h3 {
        display: flex;
        font-family: 'SourceSansPro-Regular';
        font-size: 18px;
        font-weight: 400;
        line-height: 24px;
    }
`

const Title = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;

    font-size: 18px;

    > i {
        margin-right: 8px;
    }
`

const StyledButton = styled(Button)`
    &:hover {
        opacity: .8;
    }

    &:last-child {
        background: transparent;
    }
`

const Container = styled.div`
    > h3 {
        margin-bottom: 8px;
    }

    > p {
        color: var(--red-500);
        font-size: 12px;
        margin-top: 8px;

        > i {
            margin-right: 6px;
        }
    }
`

const DeleteContainer = styled.div`
    > h3 {
        font-size: 14px;
        line-height: 16px;
        margin-bottom: 12px;

        > b {
            text-transform: uppercase;
            color: var(--red-700);
        }

        &:nth-child(2) {
            color: var(--red-700);
        }
    }
`
